"use client";
import React, { useEffect, useRef, useState } from "react";
import { TransitionLink as Link, useRouteTransition } from "./RouteTransition";
import Image from "next/image";
import { projects } from "../work/projectDetails";
import usePortfolioMotion from "./usePortfolioMotion";
import FluidMenu from "./FluidMenu";
import "./portfolio.css";
import "./experience.css";
import "./refinements.css";

export default function CaseStudy({ id }: { id: number }) {
  const root = useRef<HTMLDivElement>(null);
  const dialog = useRef<HTMLDialogElement>(null);
  const [selected, setSelected] = useState(0);
  const [archiveOpen, setArchiveOpen] = useState(true);
  const [headerVisible, setHeaderVisible] = useState(true);
  useEffect(() => {
    const header = root.current?.querySelector(".site-header");
    if (!header) return;
    const observer = new IntersectionObserver(([entry]) => {
      setHeaderVisible(entry.isIntersecting);
    });
    observer.observe(header);
    return () => observer.disconnect();
  }, []);
  const { phase } = useRouteTransition();
  const arrivedViaBubble = useRef(phase !== "idle");
  usePortfolioMotion(
    root,
    phase === "idle" || phase === "revealing",
    !arrivedViaBubble.current
  );
  const project = projects.find((p) => p.id === id)!;
  const next = projects[(projects.indexOf(project) + 1) % projects.length];
  const images = project.images?.length ? project.images : [project.image];
  const description = (project.pageDescription || project.description)
    .split(/\n\s*\n/)
    .map((block) => block.trim())
    .filter(Boolean);
  const changeImage = (delta: number) =>
    setSelected((index) => (index + delta + images.length) % images.length);
  return (
    <div ref={root} className="portfolio case-study has-fluid-menu" data-header-visible={headerVisible}>
      <FluidMenu />
      <div className="scroll-progress" aria-hidden="true" />
      <header className="site-header">
        <Link href="/" className="wordmark" aria-label="Sahidemon home">
          sahi<span>demon</span>
          <i>®</i>
        </Link>
        <Link href="/#work" className="case-back magnetic">
          ← All projects
        </Link>
        <a href="mailto:contact@sahiya.lk" className="header-contact">
          Let’s talk ↗
        </a>
      </header>
      <main>
        <section className="case-hero section-pad">
          <div className="section-label hero-enter">
            <span>
              PROJECT / {String(project.id + 1).padStart(2, "0")}
              {project.release && <b className="case-release"> / {project.release}</b>}
            </span>
            <span>
              {project.id === 2
                ? "MOBILE APPLICATION"
                : project.id < 2
                ? "AI & SYSTEMS"
                : "WEB EXPERIENCE"}
            </span>
          </div>
          <h1 className="hero-enter">
            {project.name}
            <span className="case-asterisk" aria-hidden="true">
              ✳
            </span>
          </h1>
          <div className="case-intro hero-enter">
            <p>{project.description}</p>
            <div className="case-actions">
              {project.showcase && (
                <a className="primary-link" href="#case-showcase">
                  Watch the 2.0 showcase ↓
                </a>
              )}
              {project.demo && (
                <a
                  className="primary-link"
                  href={project.demo}
                  target="_blank"
                  rel="noreferrer"
                >
                  Visit live project ↗
                </a>
              )}
              {project.github && (
                <a
                  className="quiet-link"
                  href={project.github}
                  target="_blank"
                  rel="noreferrer"
                >
                  View source ↗
                </a>
              )}
              {project.id === 2 && (
                <Link className="quiet-link" href="/documate">
                  Download app ↓
                </Link>
              )}
              <a className="quiet-link" href="#case-story">
                Explore the build ↓
              </a>
            </div>
          </div>
        </section>
        <div className="case-cover">
          <Image
            className="depth-media"
            src={project.image}
            alt={`${project.name} overview`}
            width={1600}
            height={1000}
            priority
            sizes="100vw"
          />
          <span className="cover-caption">
            {project.name} / PROJECT OVERVIEW
          </span>
        </div>
        {project.showcase && (
          <section id="case-showcase" className="case-showcase section-pad">
            <div className="section-label">
              <span>THE NEW RELEASE / IN MOTION</span>
              <span>1080P · 2 MIN 58 SEC</span>
            </div>
            <div className="case-showcase-heading">
              <h2>{project.showcase.title}</h2>
              <p>{project.showcase.description}</p>
            </div>
            <video
              controls
              playsInline
              preload="none"
              poster={project.showcase.poster}
              aria-label={`${project.name} product showcase`}
            >
              <source src={project.showcase.src} type="video/mp4" />
              <track kind="captions" src={project.showcase.captions} srcLang="en" label="English" />
              <a href={project.showcase.src}>Download the showcase video</a>
            </video>
          </section>
        )}
        <section id="case-story" className="case-story section-pad">
          <aside className="case-sidebar">
            <div className="section-label">
              <span>BEHIND THE BUILD</span>
            </div>
            <h2>
              The idea.
              <br />
              <span className="serif">The details.</span>
            </h2>
            <div className="case-stack">
              <span>BUILT WITH</span>
              {project.techNames.map((tech, i) => (
                <a
                  href={project.techLinks[i]}
                  key={tech}
                  target="_blank"
                  rel="noreferrer"
                >
                  {tech} ↗
                </a>
              ))}
            </div>
          </aside>
          <div className="case-description">
            {description.map((block, i) => {
              const lines = block
                .split("\n")
                .map((line) => line.trim())
                .filter(Boolean);
              const bullet = lines.some((line) => /^[•*-]\s/.test(line));
              const title = lines[0]?.endsWith(":") ? lines.shift() : undefined;
              return (
                <div className="case-block reveal" key={i}>
                  {title && <h3>{title.replace(/:$/, "")}</h3>}
                  {bullet ? (
                    <ul>
                      {lines.map((line, j) => (
                        <li key={j}>{line.replace(/^[•*-]\s*/, "")}</li>
                      ))}
                    </ul>
                  ) : (
                    <p>{lines.join(" ")}</p>
                  )}
                </div>
              );
            })}
          </div>
        </section>
        <section className="case-gallery section-pad">
          <div className="section-label reveal">
            <span>
              IN FOCUS / {String(images.length).padStart(2, "0")} VIEWS
            </span>
            <span>CLICK TO EXPLORE</span>
          </div>
          <h2 className="reveal">
            A closer <span className="serif">look.</span>
          </h2>
          <div className="case-gallery-grid">
            {images.map((src, i) => (
              <button
                className="case-gallery-button reveal"
                key={src}
                aria-label={`Open ${project.name} screenshot ${i + 1}${project.imageCaptions?.[i] ? `: ${project.imageCaptions[i]}` : ""}`}
                onClick={() => {
                  setSelected(i);
                  dialog.current?.showModal();
                }}
              >
                <div className="case-shot">
                  <Image
                    className="depth-media"
                    src={src}
                    alt={project.imageCaptions?.[i] || `${project.name} screenshot ${i + 1}`}
                    width={1200}
                    height={800}
                    sizes="(max-width:700px) 90vw, 46vw"
                  />
                </div>
                <span>
                  <span>{project.imageCaptions?.[i] || `FIG. ${String(i + 1).padStart(2, "0")}`}</span>
                  <span>Enlarge ↗</span>
                </span>
              </button>
            ))}
          </div>
        </section>
        {(project.videoDemo || project.videoExplanation) && (
          <section className="case-films section-pad">
            <div className="section-label reveal">
              <span>THE PROJECT IN MOTION</span>
            </div>
            <h2 className="reveal">
              See it <span className="serif">working.</span>
            </h2>
            <div className="case-video-grid">
              {project.videoDemo && (
                <div className="reveal">
                  <h3>{project.videoDemoLabel || "Demonstration"}</h3>
                  <iframe
                    src={project.videoDemo}
                    loading="lazy"
                    title={`${project.name} demonstration`}
                    allow="fullscreen; picture-in-picture"
                    allowFullScreen
                    referrerPolicy="strict-origin-when-cross-origin"
                  />
                </div>
              )}
              {project.videoExplanation && (
                <div className="reveal">
                  <h3>{project.videoExplanationLabel || "Project walkthrough"}</h3>
                  <video
                    src={project.videoExplanation}
                    controls
                    preload="none"
                    poster={project.image}
                  />
                </div>
              )}
            </div>
          </section>
        )}
        {project.archive && (
          <section className="case-archive section-pad" aria-label="Earlier versions">
            <details open={archiveOpen} onToggle={(event) => setArchiveOpen(event.currentTarget.open)}>
              <summary>
                <div>
                  <span className="case-archive-label">FROM THE ARCHIVE / ORIGINAL RELEASE</span>
                  <h2>{project.archive.title}</h2>
                  <p>Open the original demonstration and walkthrough</p>
                </div>
                <span className="case-archive-toggle" aria-hidden="true">+</span>
              </summary>
              {archiveOpen && (
                <div className="case-archive-content">
                  <p>{project.archive.description}</p>
                  <div className="case-video-grid">
                    <div>
                      <h3>Aiden 1.0 demonstration</h3>
                      <iframe
                        src={project.archive.demo}
                        title="Aiden 1.0 original YouTube demonstration"
                        loading="lazy"
                        allow="fullscreen; picture-in-picture"
                        allowFullScreen
                        referrerPolicy="strict-origin-when-cross-origin"
                      />
                    </div>
                    <div>
                      <h3>Aiden 1.0 project walkthrough</h3>
                      <video
                        src={project.archive.walkthrough}
                        poster={project.archive.poster}
                        aria-label="Aiden 1.0 original project walkthrough"
                        controls
                        playsInline
                        preload="none"
                      />
                    </div>
                  </div>
                </div>
              )}
            </details>
          </section>
        )}
        <Link href={`/projects/${next.id}`} className="case-next section-pad">
          <span className="section-label">KEEP EXPLORING / NEXT PROJECT</span>
          <span className="next-title">
            {next.name}
            <span aria-hidden="true">↗</span>
          </span>
          <Image
            src={next.image}
            alt={`${next.name} preview`}
            width={500}
            height={300}
            sizes="30vw"
          />
        </Link>
      </main>
      <footer className="site-footer">
        <Link href="/#work">← Back to all work</Link>
        <span>SAHINDU GAYANUKA / {new Date().getFullYear()}</span>
        <a href="mailto:contact@sahiya.lk">LET’S TALK ↗</a>
      </footer>
      <dialog
        className="case-lightbox"
        ref={dialog}
        data-lenis-prevent
        onKeyDown={(e) => {
          if (e.key === "ArrowRight") changeImage(1);
          if (e.key === "ArrowLeft") changeImage(-1);
        }}
      >
        <div className="lightbox-toolbar">
          <span>
            {selected + 1} / {images.length} — {project.name}
          </span>
          <button
            onClick={() => dialog.current?.close()}
            aria-label="Close screenshot"
          >
            Close ×
          </button>
        </div>
        <div className="lightbox-image">
          <Image
            src={images[selected]}
            alt={project.imageCaptions?.[selected] || `${project.name} screenshot ${selected + 1}`}
            fill
            sizes="90vw"
          />
        </div>
        <div className="lightbox-navigation">
          <button
            onClick={() => changeImage(-1)}
            aria-label="Previous screenshot"
          >
            ← Previous
          </button>
          <button onClick={() => changeImage(1)} aria-label="Next screenshot">
            Next →
          </button>
        </div>
      </dialog>
    </div>
  );
}
